<template>
  <VRow>
    <VCol cols="12">
      <VCard title="Master Data Restaurants">
        <VRow class="mb-4">
          <VCol>
            <VCardText>Manage your restaurant data efficiently using the tools below.</VCardText>
          </VCol>
          <VCol cols="auto">
            <VBtn
              @click="openPopup"
              size="small"
              class="mr-4"
            >
              Add New
              <VIcon
                class="ml-1"
                icon="ri-add-large-line"
              />
            </VBtn>
          </VCol>
        </VRow>

        <VTable>
          <thead>
            <tr>
              <th>No</th>
              <th class="text-uppercase">Restaurant Id</th>
              <th class="text-uppercase text-center">Supplier</th>
              <th class="text-uppercase text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(restaurant, index) in restaurants"
              :key="restaurant.id"
            >
              <td>{{ index + 1 }}</td>
              <td>{{ restaurant.restaurantId }}</td>
              <td class="text-center">
                <VChip
                  :color="getInventorySourceColor(restaurant.suppliers.name)"
                  dark
                  small
                >
                  {{ restaurant.suppliers.aliasName }}
                </VChip>
              </td>
              <td class="text-center">
                <VBtn
                  color="error"
                  size="small"
                  @click="openDeleteConfirmation(restaurant.id)"
                >
                  Delete
                </VBtn>
              </td>
            </tr>
          </tbody>
        </VTable>
      </VCard>
    </VCol>
  </VRow>

  <!-- Popup for adding new restaurant -->
  <v-dialog
    max-width="500"
    v-model="isPopupActive"
  >
    <v-card title="Input Master Data Restaurants">
      <v-card-text>
        <form @submit.prevent="handleSubmit">
          <VRow>
            <VCol cols="12">
              <VRow no-gutters>
                <VCol
                  cols="12"
                  md="3"
                >
                  <label for="restaurantId">Restaurant Id</label>
                </VCol>
                <VCol
                  cols="12"
                  md="9"
                >
                  <VTextField
                    id="restaurantId"
                    type="number"
                    v-model="formData.restaurantId"
                    placeholder="Enter Restaurant Id"
                    persistent-placeholder
                  />
                </VCol>
              </VRow>
            </VCol>

            <VCol cols="12">
              <VRow no-gutters>
                <VCol
                  cols="12"
                  md="3"
                >
                  <label for="inventorySource">Supplier</label>
                </VCol>
                <VCol
                  cols="12"
                  md="8"
                >
                  <v-radio-group v-model="formData.supplierId">
                    <v-radio
                      v-for="source in configStore.inventorySources"
                      :key="source.id"
                      :label="source.aliasName"
                      :value="source.id"
                    />
                  </v-radio-group>
                </VCol>
              </VRow>
            </VCol>

            <VCol cols="12">
              <VRow no-gutters>
                <VCol
                  cols="12"
                  md="3"
                />
                <VCol
                  cols="12"
                  md="9"
                >
                  <VBtn
                    type="submit"
                    class="me-4"
                    >Submit</VBtn
                  >
                  <VBtn
                    @click="closePopup"
                    color="secondary"
                    variant="outlined"
                    >Cancel</VBtn
                  >
                </VCol>
              </VRow>
            </VCol>
          </VRow>
        </form>
      </v-card-text>
    </v-card>
  </v-dialog>

  <!-- Confirmation Popup -->
  <v-dialog
    max-width="400"
    v-model="isDeletePopupActive"
  >
    <v-card>
      <v-card-title>Confirm Deletion</v-card-title>
      <v-card-text>Are you sure you want to delete this restaurant?</v-card-text>
      <v-card-actions>
        <VBtn
          color="secondary"
          variant="outlined"
          @click="closeDeletePopup"
        >
          Cancel
        </VBtn>
        <VBtn
          color="error"
          @click="confirmDelete"
        >
          Delete
        </VBtn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Alert Message -->
  <v-snackbar
    v-model="isShowSnackBar"
    multi-line
    :globalTop="true"
    :color="alertType"
    dismissible
    class="mb-4"
    @click:close="clearAlert"
  >
    {{ alertMessage }}
  </v-snackbar>
</template>
<script setup lang="ts">
import { useConfigStore } from '#imports'
import { useAlert } from '@/composables/useAlert'
import { usePopUp } from '@/composables/usePopUp'
import { useFetch } from '@vueuse/core'
import { onMounted, ref } from 'vue'

// Interfaces
interface Restaurant {
  id: number
  restaurantId: string
  suppliers: {
    id: string
    name: string
    aliasName: string
  }
  environments: {
    id: string
    name: string
    aliasName: string
  }
}
const configStore = useConfigStore()
const { alertMessage, alertType, isShowSnackBar, showAlert, clearAlert } = useAlert()
const { isPopupActive, openPopup, closePopup } = usePopUp()
// Props
const props = defineProps({
  environmentId: {
    type: Number,
    required: true,
  },
})

// Reactive state
const formData = ref({
  restaurantId: '',
  supplierId: 1,
  environmentId: props.environmentId,
})
const isDeletePopupActive = ref(false)
const selectedRestaurantId = ref<number | null>(null)
const restaurants = ref<Restaurant[]>([])

// Fetch restaurants
const fetchRestaurants = async () => {
  try {
    console.log('Fetching restaurants for environment:', props.environmentId)
    const { data } = await useFetch(`/api/restaurant?environmentId=${props.environmentId}`)
    const parsedData = JSON.parse(data.value as string)

    if (!parsedData.success) throw new Error(parsedData.message || 'Failed to fetch restaurants')

    // Set the filtered restaurants
    restaurants.value = parsedData.data || []
  } catch (err: any) {
    showAlert(err?.message || 'An error occurred while fetching restaurants.', 'error')
  }
}

// Open delete confirmation popup
const openDeleteConfirmation = (id: number) => {
  selectedRestaurantId.value = id
  isDeletePopupActive.value = true
}

// Close delete confirmation popup
const closeDeletePopup = () => {
  selectedRestaurantId.value = null
  isDeletePopupActive.value = false
}

const getInventorySourceColor = (source: string): string => {
  switch (source) {
    case 'hungry_hub':
      return 'success'
    case 'seven_room':
      return 'warning'
    case 'table_check':
      return 'secondary'
    case 'weeloy':
      return 'error'
    default:
      return 'grey'
  }
}

// Handle form submission
const handleSubmit = async () => {
  if (!formData.value.restaurantId || !formData.value.supplierId) {
    showAlert('Please fill in all required fields.', 'error')
    return
  }

  try {
    const newRestaurant = {
      ...formData.value,
    }

    const { data } = await useFetch('/api/restaurant', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newRestaurant),
    })
    const parsedData = JSON.parse(data.value as string)

    const { message, success } = (parsedData as { message: string; success: boolean }) || {}

    if (!success) throw new Error(message || 'Failed to add restaurant')

    resetForm()
    showAlert(message || 'Restaurant added successfully', 'success')
    closePopup()
    await fetchRestaurants()
  } catch (err: any) {
    showAlert(err?.message || 'An error occurred while adding the restaurant.', 'error')
  }
}

// Confirm delete
const confirmDelete = async () => {
  if (selectedRestaurantId.value === null) return

  try {
    const { data } = await useFetch(
      `/api/restaurant?id=${selectedRestaurantId.value}&environmentId=${props.environmentId}`,
      {
        method: 'DELETE',
      },
    )
    const parsedData = JSON.parse(data.value as string)

    if (!parsedData.success) throw new Error(parsedData.message || 'Failed to delete restaurant')

    showAlert(parsedData.message || 'Restaurant deleted successfully', 'success')
    await fetchRestaurants() // Refresh the list after deletion
  } catch (err: any) {
    showAlert(err?.message || 'An error occurred while deleting the restaurant.', 'error')
  } finally {
    closeDeletePopup()
  }
}

const resetForm = () => {
  formData.value.restaurantId = ''
  formData.value.supplierId = 1
  formData.value.environmentId = props.environmentId
}

// Fetch restaurants on mount
onMounted(fetchRestaurants)
</script>
